import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ClipboardPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import {
  selectIsValueFoundInToken,
  setSelectedModule,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import {
  getPatientListByPracticeId,
  resetPatientListByPracticeId,
  usePatientReducer,
} from "../../../redux/slice/patient/patientSlice";
import { checkAncilliaryAgreementsForPractice } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { fetchPracticeProductsData } from "../../../redux/slice/practice/practiceProductSlice";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import { RootState } from "../../../redux/store";
import {
  CAN_ACCESS_APPLICATION,
  IVR_STATUS,
  PRODUCT_CATEGORY,
  USER_ROLE_ENTITY_NAMES,
  PATIENT_LIST_ACTIONS,
} from "../../../constants/applicationConstants";
import { fullName, sortNames } from "../../../utils/Utility";

interface CreateIVRModalProps {
  open: boolean;
  onClose: () => void;
}

interface IVRFormData {
  practice: string | number;
  patientId: string | number;
}

const CreateIVRModal = ({ open, onClose }: CreateIVRModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { userInfo } = useAuthSlice();
  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { patientList } = usePatientReducer();

  const canCreateIVR = useSelector((state: RootState) =>
    selectIsValueFoundInToken(
      "canAccessPatientActions",
      PATIENT_LIST_ACTIONS.CREATE_IVR,
    )(state),
  );
  const canAccessIvrEmailSetting = useSelector((state: RootState) =>
    selectIsValueFoundInToken(
      "canAccessIVRActions",
      IVR_STATUS.EMAIL_SETTINGS,
    )(state),
  );

  const [step, setStep] = useState<1 | 2>(canAccessIvrEmailSetting ? 1 : 2);
  const [emailNotif, setEmailNotif] = useState(true);
  const [agreementError, setAgreementError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IVRFormData>({
    defaultValues: { practice: "", patientId: "" },
    mode: "onSubmit",
  });

  const selectedPractice = watch("practice");

  useEffect(() => {
    if (open) {
      dispatch(resetPatientListByPracticeId());
      if (practiceByUserRoleUserSlice?.length === 1) {
        const singleId = practiceByUserRoleUserSlice[0].practiceId;
        setValue("practice", singleId);
        loadPatientsForPractice(singleId.toString());
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedPractice) {
      dispatch(resetPatientListByPracticeId());
      setValue("patientId", "");
      setAgreementError("");
      loadPatientsForPractice(selectedPractice.toString());
      checkAncillaryAgreements(Number(selectedPractice));
    }
  }, [selectedPractice]);

  const loadPatientsForPractice = async (practiceId: string) => {
    await dispatch(getPatientListByPracticeId({ practiceId }));
  };

  const checkAncillaryAgreements = async (practiceId: number) => {
    await dispatch(checkAncilliaryAgreementsForPractice(practiceId));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep(canAccessIvrEmailSetting ? 1 : 2);
      setEmailNotif(true);
      setAgreementError("");
      dispatch(resetPatientListByPracticeId());
      reset();
    }, 300);
  };

  const onFormSubmit = async (data: IVRFormData) => {
    if (!data.practice) return;

    setIsSubmitting(true);
    setAgreementError("");

    try {
      const productsResponse = await dispatch(
        fetchPracticeProductsData(data.practice.toString()),
      );

      if (
        !productsResponse?.payload?.data ||
        productsResponse.payload.data.length === 0
      ) {
        setAgreementError(
          "Could not find any valid Product Agreements for the associated practice.",
        );
        setIsSubmitting(false);
        return;
      }

      const ancillaryResponse = await dispatch(
        checkAncilliaryAgreementsForPractice(Number(data.practice)),
      );
      const hasAncillary = ancillaryResponse?.payload === true;

      dispatch(setSelectedModule("IVR"));

      navigate("/ivr", {
        state: {
          practiceId: data.practice,
          ivrEmailNotification: emailNotif,
          patientData: {
            from: CAN_ACCESS_APPLICATION.PATIENT_MANAGEMENT,
            patientId: data.patientId || null,
          },
          // CS-1728 - Replacing static label to dynamic constant
          productCategory: hasAncillary ? PRODUCT_CATEGORY.CONSERVATIVE : PRODUCT_CATEGORY.ADVANCED,
        },
      });

      handleClose();
    } catch (error) {
      const alertObj: SingleAlertInfo = {
        message: "An error occurred while creating the IVR. Please try again.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    } finally {
      setIsSubmitting(false);
    }
  };

  const practiceOptions = useMemo(() => {
    if (!practiceByUserRoleUserSlice?.length) return [];
    const sorted = sortNames(practiceByUserRoleUserSlice, "practiceName");
    return sorted.map((p: PracticeInfo) => ({
      value: p.practiceId,
      label: p.practiceName,
    }));
  }, [practiceByUserRoleUserSlice]);

  const patientOptions = useMemo(() => {
    if (!patientList?.length) return [];
    return patientList.map((p: any) => ({
      value: p.patientId,
      label:
        fullName("", p.firstName, p.middleName, p.lastName) +
        " — DOB: " +
        p.birthDate,
    }));
  }, [patientList]);

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const EmailNotifBadge = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.8,
        px: 1.4,
        py: 0.6,
        borderRadius: "20px",
        alignSelf: "flex-start",
        backgroundColor: emailNotif
          ? "rgba(20,184,166,0.08)"
          : isDark
            ? "rgba(255,255,255,0.04)"
            : "rgba(0,0,0,0.04)",
        border: `1px solid ${emailNotif ? "rgba(20,184,166,0.2)" : tokens.border}`,
      }}
    >
      <NotificationsNoneIcon
        sx={{
          fontSize: "0.85rem",
          color: emailNotif ? tokens.accent : tokens.textSecondary,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.76rem",
          fontWeight: 600,
          color: emailNotif ? tokens.accent : tokens.textSecondary,
        }}
      >
        Email notifications {emailNotif ? "ON" : "OFF"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <ClipboardPlus size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Create IVR
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
          >
            {step === 1
              ? "Configure notification settings"
              : "Select practice & patient"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {step === 1 && (
        <>
          <Box
            sx={{
              px: 2.8,
              py: 2.8,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {canAccessIvrEmailSetting && (
              <>
                <Box
                  sx={{
                    backgroundColor: tokens.cardBg,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: "12px",
                    px: 2.2,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "9px",
                        backgroundColor: emailNotif
                          ? "rgba(20,184,166,0.12)"
                          : isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                        border: `1px solid ${
                          emailNotif ? "rgba(20,184,166,0.25)" : tokens.border
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <NotificationsNoneIcon
                        sx={{
                          fontSize: "1rem",
                          color: emailNotif
                            ? tokens.accent
                            : tokens.textSecondary,
                          transition: "color 0.2s",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          color: tokens.textPrimary,
                          lineHeight: 1.3,
                        }}
                      >
                        Email notifications
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          color: tokens.textSecondary,
                          mt: 0.1,
                        }}
                      >
                        Notify relevant parties on IVR creation
                      </Typography>
                    </Box>
                  </Box>

                  <Switch
                    checked={emailNotif}
                    onChange={(e) => setEmailNotif(e.target.checked)}
                    sx={{
                      width: 54,
                      height: 28,
                      padding: 0,
                      flexShrink: 0,
                      "& .MuiSwitch-switchBase": {
                        padding: 0,
                        margin: "3px",
                        transitionDuration: "200ms",
                        "&.Mui-checked": {
                          transform: "translateX(26px)",
                          color: "#fff",
                          "& + .MuiSwitch-track": {
                            backgroundColor: tokens.accent,
                            opacity: 1,
                            border: 0,
                          },
                        },
                      },
                      "& .MuiSwitch-thumb": {
                        boxSizing: "border-box",
                        width: 22,
                        height: 22,
                      },
                      "& .MuiSwitch-track": {
                        borderRadius: 14,
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(0,0,0,0.15)",
                        opacity: 1,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: -1.5 }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      color: emailNotif ? tokens.accent : tokens.textSecondary,
                      transition: "color 0.2s",
                    }}
                  >
                    {emailNotif ? "ON" : "OFF"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Box sx={footerSx}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  <AddCircleOutlineIcon sx={{ fontSize: "1rem !important" }} />
                }
                onClick={() => setStep(2)}
                sx={primaryBtnSx}
              >
                Create IVR
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                onClick={handleClose}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Box sx={{ ...bodySx, pt: 2 }}>
            {canAccessIvrEmailSetting && <EmailNotifBadge />}

            {agreementError && (
              <Typography
                sx={{
                  color: tokens.error,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: isDark
                    ? "rgba(239,68,68,0.08)"
                    : "rgba(239,68,68,0.06)",
                  border: "1px solid rgba(239,68,68,0.2)",
                }}
              >
                {agreementError}
              </Typography>
            )}

            <Box>
              <FieldLabel label="Practice Name" required />
              <Box sx={dropdownWrapSx(!!errors.practice)}>
                <CustomDropdownField
                  name="practice"
                  control={control}
                  options={practiceOptions}
                  rules={{ required: "Please select a practice to continue." }}
                  size="small"
                  placeholderText="Select a practice..."
                  sortOptions={false}
                  disabled={practiceOptions.length === 1}
                  onChangeSelect={() => setAgreementError("")}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel label="Patient" required />
              <Box sx={dropdownWrapSx(!!errors.patientId)}>
                <CustomDropdownField
                  name="patientId"
                  control={control}
                  options={patientOptions}
                  rules={{ required: "Please select a patient to continue." }}
                  size="small"
                  placeholderText={
                    !selectedPractice
                      ? "Select a practice first..."
                      : patientOptions.length === 0
                        ? "No active patients found..."
                        : "Select a patient..."
                  }
                  sortOptions={false}
                  disabled={!selectedPractice}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={footerSx}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                startIcon={
                  <LocalHospitalOutlinedIcon
                    sx={{ fontSize: "1rem !important" }}
                  />
                }
                sx={primaryBtnSx}
              >
                {isSubmitting ? "Creating..." : "Create IVR"}
              </Button>
              {canAccessIvrEmailSetting ? (
              <Button
                fullWidth
                variant="outlined"
                startIcon={
                  <ArrowBackIcon sx={{ fontSize: "1rem !important" }} />
                }
                onClick={() => {
                  setStep(canAccessIvrEmailSetting ? 1 : 2);
                  setAgreementError("");
                }}
                sx={{
                  ...primaryBtnSx,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  color: tokens.textSecondary,
                  border: `1px solid ${tokens.border}`,
                  "&:hover": {
                    backgroundColor: "rgba(20,184,166,0.05)",
                    borderColor: tokens.accent,
                    color: tokens.accent,
                    boxShadow: "none",
                  },
                }}
              >
                Back
              </Button>
              ):(
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                  onClick={handleClose}
                  sx={cancelBtnSx}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </form>
      )}
    </Dialog>
  );
};

export default CreateIVRModal;